/**
 * Turns a `ShellState` into the draw list plus the hit-test that inverts it.
 * Both come from the same layout call, so a button can never be drawn
 * somewhere the mouse doesn't find it.
 */

import { activeScreen, activeSelection, type MenuScreen, type MenuTree, type ShellState } from "../model/menu-model.ts";
import { MAIN_MENU_ID } from "../model/red-baron-shell.ts";
import type { DrawOp, FontId } from "./draw-ops.ts";
import { rectContains } from "./draw-ops.ts";
import { buttonRect, mainMenuOps } from "./menu-chrome.ts";
import { layoutPopup, popupOps, popupRowAt } from "./popup-chrome.ts";
import type { FontAtlas } from "./text-metrics.ts";

export interface ScreenView {
  readonly ops: readonly DrawOp[];
  /** Index of the item under a logical point, or `undefined` for dead space. */
  hitTest(x: number, y: number): number | undefined;
}

function fontOf(fonts: ReadonlyMap<FontId, FontAtlas>, id: FontId): FontAtlas {
  const atlas = fonts.get(id);
  if (!atlas) throw new Error(`shell: font atlas ${id} was never loaded`);
  return atlas;
}

function boardView(screen: MenuScreen, selected: number, fonts: ReadonlyMap<FontId, FontAtlas>): ScreenView {
  const labels = screen.items.map((item) => item.label);
  return {
    ops: mainMenuOps({
      title: screen.title,
      labels,
      selected,
      font: fontOf(fonts, "FONT1"),
      bannerFont: fontOf(fonts, "FONT2"),
    }),
    hitTest(x, y) {
      const index = labels.findIndex((_, i) => rectContains(buttonRect(i), x, y));
      return index < 0 ? undefined : index;
    },
  };
}

export function buildScreenView(tree: MenuTree, state: ShellState, fonts: ReadonlyMap<FontId, FontAtlas>): ScreenView {
  const root = tree.screens.get(MAIN_MENU_ID);
  const screen = activeScreen(tree, state);
  if (!root || !screen) return { ops: [], hitTest: () => undefined };

  const base = boardView(root, state.selection[0] ?? 0, fonts);
  if (screen.id === MAIN_MENU_ID) return base;

  const content = { title: screen.title, body: screen.body, labels: screen.items.map((item) => item.label) };
  const popupFonts = { title: fontOf(fonts, "FONT1"), row: fontOf(fonts, "FONT3") };
  const layout = layoutPopup(content, popupFonts);
  return {
    ops: [...base.ops, ...popupOps(content, layout, activeSelection(state), popupFonts)],
    hitTest: (x, y) => popupRowAt(layout, x, y),
  };
}
